import React from 'react';
import { Box, TextField, Typography, Button } from '@mui/material';
import { AdminFormSection, adminFieldSx, useAdminPageTheme } from '../../../pages/admin/adminUi';

const DIFFICULTY_DEFAULTS = {
  easy: { cpuTimeLimit: 2, wallTimeLimit: 5, memoryLimitMb: 256, stackLimitMb: 64, maxProcesses: 64 },
  medium: { cpuTimeLimit: 2, wallTimeLimit: 6, memoryLimitMb: 256, stackLimitMb: 64, maxProcesses: 64 },
  hard: { cpuTimeLimit: 3, wallTimeLimit: 10, memoryLimitMb: 512, stackLimitMb: 128, maxProcesses: 64 },
};

const kbToMb = (kb) => (kb != null ? Math.round(kb / 1024) : '');

const ProblemFormExecutionLimits = ({ limits, difficulty, onChange }) => {
  const { muted } = useAdminPageTheme();

  const setField = (field, value) => {
    onChange({ ...limits, [field]: value });
  };

  const applyDifficultyDefaults = () => {
    const preset = DIFFICULTY_DEFAULTS[difficulty] || DIFFICULTY_DEFAULTS.easy;
    onChange({
      ...limits,
      ...preset,
      cpuExtraTime: limits?.cpuExtraTime ?? 0.5,
    });
  };

  return (
    <AdminFormSection
      title="Execution limits"
      description="CPU, wall-clock, and memory caps sent to Judge0 on every run and submit."
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: muted }}>
          Leave blank to use platform defaults for {difficulty || 'easy'} problems on save.
        </Typography>
        <Button size="small" onClick={applyDifficultyDefaults} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Reset to {difficulty || 'easy'} preset
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
        <TextField
          label="CPU time (sec)"
          type="number"
          size="small"
          inputProps={{ min: 0.5, max: 15, step: 0.5 }}
          helperText="Max CPU seconds per test"
          value={limits?.cpuTimeLimit ?? ''}
          onChange={(e) => setField('cpuTimeLimit', e.target.value === '' ? undefined : parseFloat(e.target.value))}
          sx={adminFieldSx}
        />
        <TextField
          label="CPU extra time (sec)"
          type="number"
          size="small"
          inputProps={{ min: 0, max: 5, step: 0.1 }}
          helperText="Grace period after CPU limit"
          value={limits?.cpuExtraTime ?? ''}
          onChange={(e) => setField('cpuExtraTime', e.target.value === '' ? undefined : parseFloat(e.target.value))}
          sx={adminFieldSx}
        />
        <TextField
          label="Wall time (sec)"
          type="number"
          size="small"
          inputProps={{ min: 1, max: 30, step: 0.5 }}
          helperText="Max wall-clock seconds"
          value={limits?.wallTimeLimit ?? ''}
          onChange={(e) => setField('wallTimeLimit', e.target.value === '' ? undefined : parseFloat(e.target.value))}
          sx={adminFieldSx}
        />
        <TextField
          label="Memory (MB)"
          type="number"
          size="small"
          inputProps={{ min: 16, max: 1024, step: 16 }}
          helperText={
            limits?.memoryLimitKb
              ? `Stored: ${kbToMb(limits.memoryLimitKb)} MB`
              : 'RAM limit for the process'
          }
          value={limits?.memoryLimitMb ?? (limits?.memoryLimitKb ? kbToMb(limits.memoryLimitKb) : '')}
          onChange={(e) =>
            setField('memoryLimitMb', e.target.value === '' ? undefined : parseInt(e.target.value, 10))
          }
          sx={adminFieldSx}
        />
        <TextField
          label="Stack (MB)"
          type="number"
          size="small"
          inputProps={{ min: 8, max: 256, step: 8 }}
          value={limits?.stackLimitMb ?? (limits?.stackLimitKb ? kbToMb(limits.stackLimitKb) : '')}
          onChange={(e) =>
            setField('stackLimitMb', e.target.value === '' ? undefined : parseInt(e.target.value, 10))
          }
          sx={adminFieldSx}
        />
        <TextField
          label="Max processes / threads"
          type="number"
          size="small"
          inputProps={{ min: 1, max: 120, step: 1 }}
          value={limits?.maxProcesses ?? ''}
          onChange={(e) =>
            setField('maxProcesses', e.target.value === '' ? undefined : parseInt(e.target.value, 10))
          }
          sx={adminFieldSx}
        />
      </Box>
    </AdminFormSection>
  );
};

export default ProblemFormExecutionLimits;
